import { QueryClient } from '@tanstack/react-query';

/**
 * TanStack Query Client Configuration
 *
 * Configures default options for queries and mutations.
 * See ADR-010 for architecture rationale.
 */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered stale after 1 minute
      staleTime: 60 * 1000,

      // Retry failed requests once (helps with transient network issues)
      retry: 1,

      // Don't refetch when window regains focus
      // (can be noisy in dev, enable per-query if needed)
      refetchOnWindowFocus: false,

      // Don't refetch on mount if data is fresh
      refetchOnMount: true,

      // Keep failed queries in cache for 0ms (immediate removal)
      gcTime: 1000 * 60 * 5, // 5 minutes (formerly cacheTime)
    },
    mutations: {
      // Retry mutations once
      retry: 1,
    },
  },
});

/**
 * Query Keys
 *
 * Centralized query key factory for type safety and consistency.
 * Use these constants in query hooks to avoid typos and ensure invalidation works correctly.
 *
 * @example
 * ```ts
 * useQuery({
 *   queryKey: queryKeys.users.me,
 *   queryFn: () => authenticatedFetch('/v1/users/me')
 * });
 * ```
 */
export const queryKeys = {
  // User queries
  users: {
    all: ['users'] as const,
    me: ['users', 'me'] as const,
    detail: (id: string) => ['users', id] as const,
  },

  // Health data queries
  health: {
    all: ['health'] as const,
    measurements: (userId: string, dateRange?: string) =>
      ['health', 'measurements', userId, dateRange] as const,
    metrics: (userId: string) => ['health', 'metrics', userId] as const,
  },

  // Coach/client queries
  clients: {
    all: ['clients'] as const,
    detail: (id: string) => ['clients', id] as const,
    byCoach: (coachId: string) => ['clients', 'coach', coachId] as const,
  },

  // Admin queries
  admin: {
    users: ['admin', 'users'] as const,
    invitations: ['admin', 'invitations'] as const,
    coaches: ['admin', 'coaches'] as const,
    stats: ['admin', 'stats'] as const,
  },
} as const;
